import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import getHTMLTextContent from "@/lib/utils/dom/get-html-text-content.ts";
import { useBoolean } from "@/hooks/use-boolean.ts";
import { useCurrentShopLocalesAvailables } from "@/lib/translator";
import useUpdateCustomMessage from "@babackapp/private-api-client/client/use-update-portal-custom-message";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { PropsWithChildren, useMemo } from "react";
import {
  CustomKey,
  CustomMessagesSettingsForm,
} from "./custom-message-settings-form.tsx";
import type {
  ShopPreferencesUserPortalDto,
  TextDto,
} from "@babackapp/private-api-client/types";

type UpdateCustomMessageDialogProps = { plain?: boolean } & {
  customKey: CustomKey;
  disabled?: boolean;
};

export function UpdateCustomMessageDialog({
  children,
  customKey,
  disabled,
  plain,
}: PropsWithChildren<UpdateCustomMessageDialogProps>) {
  const t = useTranslation();
  const open = useBoolean(false);
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateCustomMessage } = useUpdateCustomMessage(shop);
  const languages = useCurrentShopLocalesAvailables();

  const values = useMemo(() => {
    const source = (shop.preferences.userPortal[
      customKey as keyof ShopPreferencesUserPortalDto
    ] ?? []) as TextDto[];
    const available = source.reduce(
      (acc, curr) => {
        acc[curr.lang] = curr.text;
        return acc;
      },
      {} as Record<string, string>,
    );

    return languages.map((lang) => {
      const raw = available[lang.code] ?? "";
      return {
        lang: lang.code,
        text: plain ? getHTMLTextContent(raw) : raw,
      };
    });
  }, [shop, customKey, languages, plain]);

  return (
    <Dialog open={open.value} onOpenChange={open.setValue}>
      <DialogTrigger disabled={disabled}>{children}</DialogTrigger>
      <DialogContent className="pt-8 max-w-[700px] w-[700px]">
        <DialogHeader className="flex flex-col justify-start items-start">
          <DialogTitle>
            {t(`settings.customMessage.${customKey}` as Translation)}
          </DialogTitle>
          <DialogDescription>
            <span>{t("settings.customMessage.subtitle")}</span>
          </DialogDescription>
        </DialogHeader>
        <div>
          <div className="font-semibold"></div>
          <CustomMessagesSettingsForm
            customKey={customKey}
            shopId={shop.id}
            languages={languages.map((l) => l.code)}
            defaultValues={{ values }}
            plain={plain}
            onSubmit={async (data) => {
              await updateCustomMessage({
                key: customKey,
                value: data
                  .filter((d) => d.text.trim().length > 0)
                  .map((d) => ({
                    lang: d.lang,
                    // TODO: revert hack to replace &nbsp; added by the quill editor when fixed in the component
                    text: plain ? d.text : d.text.replace(/&nbsp;/g, " "),
                  })),
              });
              open.setFalse();
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
